import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import Layout from '../../components/Layout';
import { API_ENDPOINTS } from '../../config/api';
import { useAppSelector } from '../../hooks/useAppSelector';
import '../../config/axiosConfig';
import RestroLiteMetricsDashboard from './RestroLiteMetricsDashboard';
import RestroLiteAppReleasePanel from './RestroLiteAppReleasePanel';
import RestroLiteFcmPushPanel from './RestroLiteFcmPushPanel';

const FILTERS = [
  { id: 'all', label: 'All shops' },
  { id: 'active', label: 'Active' },
  { id: 'expiring', label: 'Expiring soon (7d)' },
  { id: 'expired', label: 'Expired / blocked' },
];

const STATUS_STYLES = {
  ACTIVE: 'bg-emerald-100 text-emerald-800',
  EXPIRING_TODAY: 'bg-amber-100 text-amber-900',
  GRACE: 'bg-orange-100 text-orange-800',
  EXPIRED: 'bg-red-100 text-red-800',
};

const todayKey = () => new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' });

const formatAmount = (n) =>
  `₹${Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;

const BUSINESS_TYPE_OPTIONS = [
  { value: 'restaurant', label: 'Restaurant / café' },
  { value: 'retail', label: 'Retail / grocery / shop' },
];

const SUBSCRIPTION_PLAN_OPTIONS = [
  { value: '1month', label: '1 month', months: 1, amount: 500 },
  { value: '6month', label: '6 months', months: 6, amount: 2500 },
  { value: '1year', label: '1 year', months: 12, amount: 5000 },
];

const addMonthsToDateKey = (dateKey, months) => {
  if (!dateKey || !months) return '';
  const [y, m, d] = dateKey.split('-').map(Number);
  const totalMonths = m - 1 + months;
  const newYear = y + Math.floor(totalMonths / 12);
  const normalizedMonth = ((totalMonths % 12) + 12) % 12;
  const lastDay = new Date(Date.UTC(newYear, normalizedMonth + 1, 0)).getUTCDate();
  const day = Math.min(d, lastDay);
  const mm = String(normalizedMonth + 1).padStart(2, '0');
  const dd = String(day).padStart(2, '0');
  return `${newYear}-${mm}-${dd}`;
};

const subscriptionBaseDate = (form) => form.lastPaymentDate || form.serviceStartDate || todayKey();

const endDateForPlan = (baseDateKey, plan) => {
  const opt = SUBSCRIPTION_PLAN_OPTIONS.find((o) => o.value === plan);
  if (!opt || !baseDateKey) return '';
  return addMonthsToDateKey(baseDateKey, opt.months);
};

const subscriptionPayable = (amount, discount, referralCredits = 0) => {
  const base = Math.max(0, Number(amount) || 0);
  const disc = Math.max(0, Number(discount) || 0);
  const afterDiscount = Math.max(0, Math.round((base - disc) * 100) / 100);
  const credits = Math.min(Math.max(0, Number(referralCredits) || 0), afterDiscount);
  return Math.max(0, Math.round((afterDiscount - credits) * 100) / 100);
};

const applySubscriptionPlan = (form, plan) => {
  if (!plan) return { ...form, subscriptionPlan: '' };
  const base = subscriptionBaseDate(form);
  const opt = SUBSCRIPTION_PLAN_OPTIONS.find((o) => o.value === plan);
  return {
    ...form,
    subscriptionPlan: plan,
    subscriptionEndDate: endDateForPlan(base, plan),
    subscriptionAmount: opt ? String(opt.amount) : form.subscriptionAmount,
  };
};

const businessTypeLabel = (value) =>
  BUSINESS_TYPE_OPTIONS.find((o) => o.value === value)?.label || 'Restaurant / café';

const emptyForm = () => {
  const serviceStartDate = todayKey();
  return {
    ownerName: '',
    shopName: '',
    mobileNumber: '',
    businessType: 'restaurant',
    address: '',
    serviceStartDate,
    lastPaymentDate: '',
    subscriptionPlan: '1month',
    subscriptionEndDate: endDateForPlan(serviceStartDate, '1month'),
    subscriptionAmount: '500',
    subscriptionDiscount: '',
    referralCredits: '',
    referralCode: '',
    paymentLink: '',
    syncEmail: '',
    securityPin: '',
    notes: '',
    isActive: true,
    onboardedByEmpId: '',
    onboardedByName: '',
  };
};

export default function RestroLiteAdminPage({ employeeMode = false }) {
  const { user } = useAppSelector((state) => state.auth);
  const perms = user?.permissions;
  const canAdd = !employeeMode || perms?.actions?.addShop;
  const canEdit = !employeeMode || perms?.actions?.editShop;
  const canExtend = !employeeMode || perms?.actions?.extendSubscription;
  const canForceLogout = !employeeMode || perms?.actions?.forceLogoutShop;
  const canDelete = !employeeMode || perms?.actions?.deleteShop;
  const canEditPaymentLink = !employeeMode || perms?.actions?.editPaymentLink;

  const [tenants, setTenants] = useState([]);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [graceDays, setGraceDays] = useState(2);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [editingId, setEditingId] = useState(null);
  const [extendId, setExtendId] = useState(null);
  const [extendDate, setExtendDate] = useState('');
  const [busy, setBusy] = useState(false);
  const [statsRefresh, setStatsRefresh] = useState(0);
  const [showSecurityPin, setShowSecurityPin] = useState(false);
  const [companyEmployees, setCompanyEmployees] = useState([]);

  const bumpStats = () => setStatsRefresh((n) => n + 1);

  useEffect(() => {
    if (employeeMode) return;
    axios
      .get(API_ENDPOINTS.COMPANY_EMPLOYEES('', 'true'))
      .then((res) => setCompanyEmployees(res.data.employees || []))
      .catch(() => setCompanyEmployees([]));
  }, [employeeMode]);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await axios.get(API_ENDPOINTS.RESTRO_LITE_TENANTS(filter));
      setTenants(res.data.tenants || []);
      if (res.data.graceDays != null) setGraceDays(res.data.graceDays);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load PocketBillingAI shops');
      setTenants([]);
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    load();
  }, [load]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return tenants;
    return tenants.filter(
      (t) =>
        t.shopName?.toLowerCase().includes(q) ||
        t.ownerName?.toLowerCase().includes(q) ||
        t.mobileNumber?.includes(q) ||
        t.onboardedByEmpId?.toLowerCase().includes(q) ||
        t.onboardedByName?.toLowerCase().includes(q)
    );
  }, [tenants, search]);

  const pickOnboardingEmployee = (employeeId) => {
    if (!employeeId) {
      setForm((f) => ({ ...f, onboardedByEmpId: '', onboardedByName: '' }));
      return;
    }
    const emp = companyEmployees.find((e) => e.id === employeeId);
    if (emp) {
      setForm((f) => ({
        ...f,
        onboardedByEmpId: emp.empId,
        onboardedByName: emp.name,
      }));
    }
  };

  const employeeOnboardingDefaults = () => ({
    onboardedByEmpId: String(user?.empId || '').toUpperCase(),
    onboardedByName: String(user?.name || '').trim(),
  });

  const openCreate = () => {
    setEditingId(null);
    const base = emptyForm();
    if (employeeMode && user?.empId) {
      Object.assign(base, employeeOnboardingDefaults());
    }
    setForm(base);
    setShowSecurityPin(false);
    setShowForm(true);
  };

  const openEdit = (t) => {
    setEditingId(t.id);
    setShowSecurityPin(false);
    setForm({
      ownerName: t.ownerName || '',
      shopName: t.shopName || '',
      mobileNumber: t.mobileNumber || '',
      businessType: t.businessType === 'retail' ? 'retail' : 'restaurant',
      address: t.address || '',
      serviceStartDate: t.serviceStartDate || '',
      lastPaymentDate: t.lastPaymentDate || '',
      subscriptionPlan: t.subscriptionPlan || '',
      subscriptionEndDate: t.subscriptionEndDate || '',
      subscriptionAmount: t.subscriptionAmount != null ? String(t.subscriptionAmount) : '',
      subscriptionDiscount:
        t.subscriptionDiscount != null && Number(t.subscriptionDiscount) > 0
          ? String(t.subscriptionDiscount)
          : '',
      referralCode: t.referredByMobile || '',
      referralCredits:
        t.referralCredits != null && Number(t.referralCredits) > 0
          ? String(t.referralCredits)
          : '',
      referralLocked: Boolean(t.lastPaymentDate || t.referralRewardPaid),
      paymentLink: t.paymentLink || '',
      syncEmail: t.syncEmail || '',
      securityPin: t.securityPin || '',
      notes: t.notes || '',
      isActive: t.isActive !== false,
      onboardedByEmpId: t.onboardedByEmpId || '',
      onboardedByName: t.onboardedByName || '',
    });
    setShowForm(true);
  };

  const saveTenant = async (e) => {
    e.preventDefault();
    const discount = Math.max(0, Number(form.subscriptionDiscount) || 0);
    const credits = Math.max(0, Number(form.referralCredits) || 0);
    const base = Math.max(0, Number(form.subscriptionAmount) || 0);
    if (discount > base) {
      alert('Discount cannot be greater than the subscription amount.');
      return;
    }
    const afterDiscount = Math.max(0, base - discount);
    if (credits > afterDiscount) {
      alert('Referral amount credit cannot be greater than the amount after PocketBillingAI discount.');
      return;
    }
    setBusy(true);
    try {
      const { syncEmail: _ignored, referralLocked: _lock, ...payload } = form;
      payload.subscriptionDiscount = discount > 0 ? String(discount) : '0';
      payload.referralCredits = String(credits);
      if (form.referralLocked) {
        delete payload.referralCode;
      }
      if (!canEditPaymentLink) {
        delete payload.paymentLink;
      }
      if (editingId) {
        await axios.put(API_ENDPOINTS.RESTRO_LITE_TENANT(editingId), payload);
      } else {
        await axios.post(API_ENDPOINTS.RESTRO_LITE_TENANTS_CREATE, payload);
      }
      setShowForm(false);
      await load();
      bumpStats();
    } catch (err) {
      alert(err.response?.data?.message || err.message);
    } finally {
      setBusy(false);
    }
  };

  const saveExtend = async (e) => {
    e.preventDefault();
    if (!extendId || !extendDate) return;
    setBusy(true);
    try {
      await axios.patch(API_ENDPOINTS.RESTRO_LITE_EXTEND(extendId), {
        subscriptionEndDate: extendDate,
        isActive: true,
      });
      setExtendId(null);
      setExtendDate('');
      await load();
      bumpStats();
    } catch (err) {
      alert(err.response?.data?.message || err.message);
    } finally {
      setBusy(false);
    }
  };

  const forceLogoutShop = async (t) => {
    if (
      !window.confirm(
        `Sign out "${t.shopName}" on all devices?\n\nThe shop owner will be logged out immediately and must sign in again. Use this if they stop paying or no longer use the app.`
      )
    ) {
      return;
    }
    setBusy(true);
    try {
      const res = await axios.post(API_ENDPOINTS.RESTRO_LITE_FORCE_LOGOUT(t.id));
      alert(res.data?.message || 'Shop owner signed out.');
      await load();
      bumpStats();
    } catch (err) {
      alert(err.response?.data?.message || err.message);
    } finally {
      setBusy(false);
    }
  };

  const removeTenant = async (id, name) => {
    if (!window.confirm(`Delete PocketBillingAI shop "${name}"?`)) return;
    try {
      await axios.delete(API_ENDPOINTS.RESTRO_LITE_TENANT(id));
      await load();
      bumpStats();
    } catch (err) {
      alert(err.response?.data?.message || err.message);
    }
  };

  const renderStatusBadge = (t) => (
    <>
      <span
        className={`inline-block px-2 py-0.5 rounded text-xs font-semibold ${
          STATUS_STYLES[t.status] || 'bg-stone-100'
        }`}
      >
        {t.status}
        {!t.isActive ? ' · off' : ''}
      </span>
      {t.daysUntilExpiry != null && t.daysUntilExpiry >= 0 && t.status === 'ACTIVE' && (
        <span className="block text-xs text-stone-500 mt-0.5">
          {t.daysUntilExpiry}d left
        </span>
      )}
    </>
  );

  const renderTenantActions = (t, layout = 'stacked') => (
    <div
      className={
        layout === 'inline'
          ? 'flex flex-wrap justify-end gap-x-3 gap-y-1'
          : 'flex flex-wrap gap-2 pt-2 border-t border-stone-100 mt-2'
      }
    >
      {canExtend && (
        <button
          type="button"
          className="text-indigo-600 text-xs font-medium px-2 py-1 rounded hover:bg-indigo-50"
          onClick={() => {
            setExtendId(t.id);
            setExtendDate(t.subscriptionEndDate || '');
          }}
        >
          Extend
        </button>
      )}
      {canEdit && (
        <button
          type="button"
          className="text-stone-600 text-xs px-2 py-1 rounded hover:bg-stone-50"
          onClick={() => openEdit(t)}
        >
          Edit
        </button>
      )}
      {canForceLogout && (
        <button
          type="button"
          className="text-amber-700 text-xs font-medium px-2 py-1 rounded hover:bg-amber-50"
          onClick={() => forceLogoutShop(t)}
          disabled={busy}
        >
          Logout shop
        </button>
      )}
      {canDelete && (
        <button
          type="button"
          className="text-red-600 text-xs px-2 py-1 rounded hover:bg-red-50"
          onClick={() => removeTenant(t.id, t.shopName)}
        >
          Delete
        </button>
      )}
    </div>
  );

  return (
    <Layout>
      <div className="max-w-6xl mx-auto px-3 sm:px-4 py-6 sm:py-8 space-y-5 sm:space-y-6 min-w-0">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
          <div className="min-w-0">
            <p className="text-xs font-semibold text-indigo-600 uppercase tracking-wide">
              PocketBillingAI
            </p>
            <h1 className="text-xl sm:text-2xl font-bold text-stone-900">
              {employeeMode ? 'My PocketBillingAI shops' : 'PocketBillingAI subscription control'}
            </h1>
            <p className="text-sm text-stone-500 mt-1">
              {employeeMode
                ? 'Shops you onboarded. Grace period after expiry: '
                : 'Onboard shops, set subscription end dates, and track renewals. Grace period after expiry: '}
              <strong>{graceDays} days</strong>.
            </p>
          </div>
          {canAdd && (
            <button
              type="button"
              onClick={openCreate}
              className="w-full sm:w-auto shrink-0 px-4 py-2.5 rounded-lg bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700"
            >
              + Add shop
            </button>
          )}
        </div>

        {!employeeMode && (
          <RestroLiteMetricsDashboard
            refreshToken={statsRefresh}
            onFilterFromCard={(filterId) => setFilter(filterId)}
          />
        )}

        {!employeeMode && <RestroLiteAppReleasePanel />}

        {!employeeMode && <RestroLiteFcmPushPanel />}

        <div className="flex flex-wrap gap-2">
          {FILTERS.map((f) => (
            <button
              key={f.id}
              type="button"
              onClick={() => setFilter(f.id)}
              className={`px-2.5 sm:px-3 py-1.5 rounded-lg text-xs sm:text-sm border ${
                filter === f.id
                  ? 'bg-indigo-100 border-indigo-400 text-indigo-900 font-medium'
                  : 'border-stone-200 text-stone-600 hover:bg-stone-50'
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>

        <input
          type="search"
          placeholder="Search shop, owner, mobile…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full border border-stone-200 rounded-lg px-3 py-2 text-sm"
        />

        {error && (
          <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
            {error}
          </p>
        )}

        {loading ? (
          <p className="text-stone-500">Loading…</p>
        ) : (
          <div className="min-w-0">
            {/* Mobile: card list */}
            <div className="md:hidden space-y-3">
              {filtered.length === 0 ? (
                <div className="bg-white rounded-xl border border-stone-200 px-4 py-8 text-center text-stone-500 text-sm shadow-sm">
                  No shops found
                </div>
              ) : (
                filtered.map((t) => (
                  <div
                    key={t.id}
                    className="bg-white rounded-xl border border-stone-200 p-4 shadow-sm border-l-4 border-l-indigo-400"
                  >
                    <div className="flex items-start justify-between gap-3 mb-3">
                      <div className="min-w-0">
                        <p className="font-semibold text-stone-900 truncate">{t.shopName}</p>
                        <p className="text-sm text-stone-600 truncate">{t.ownerName}</p>
                        <p className="text-sm text-stone-500 tabular-nums mt-0.5">{t.mobileNumber}</p>
                      </div>
                      <div className="shrink-0 text-right">{renderStatusBadge(t)}</div>
                    </div>
                    <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1.5 text-xs text-stone-600">
                      <dt className="text-stone-400">Business</dt>
                      <dd className="font-medium text-stone-700">{businessTypeLabel(t.businessType)}</dd>
                      {!employeeMode && (
                        <>
                          <dt className="text-stone-400">Onboarded by</dt>
                          <dd className="font-medium text-stone-700">
                            {t.onboardedByEmpId ? (
                              <>
                                <span className="font-mono">{t.onboardedByEmpId}</span>
                                {t.onboardedByName ? (
                                  <span className="block text-stone-500 font-sans">{t.onboardedByName}</span>
                                ) : null}
                              </>
                            ) : (
                              '—'
                            )}
                          </dd>
                        </>
                      )}
                      <dt className="text-stone-400">Service start</dt>
                      <dd className="tabular-nums">{t.serviceStartDate || '—'}</dd>
                      <dt className="text-stone-400">Last payment</dt>
                      <dd className="tabular-nums">{t.lastPaymentDate || '—'}</dd>
                      <dt className="text-stone-400">Valid until</dt>
                      <dd className="tabular-nums font-medium">{t.subscriptionEndDate}</dd>
                      <dt className="text-stone-400">Amount</dt>
                      <dd className="tabular-nums font-semibold text-stone-800">
                        {formatAmount(t.subscriptionPayableAmount ?? t.subscriptionAmount)}
                        {Number(t.subscriptionDiscount) > 0 && (
                          <span className="block text-xs font-normal text-emerald-700">
                            −{formatAmount(t.subscriptionDiscount)} PocketBillingAI discount
                          </span>
                        )}
                      </dd>
                    </dl>
                    {renderTenantActions(t, 'row')}
                  </div>
                ))
              )}
            </div>

            {/* Desktop: scrollable table */}
            <div className="hidden md:block bg-white rounded-xl border border-stone-200 overflow-x-auto shadow-sm">
              <table className="w-full text-sm min-w-[960px]">
                <thead className="bg-stone-50 text-stone-600">
                  <tr>
                    <th className="text-left px-4 py-3 whitespace-nowrap">Shop</th>
                    <th className="text-left px-4 py-3 whitespace-nowrap">Owner</th>
                    <th className="text-left px-4 py-3 whitespace-nowrap">Mobile</th>
                    <th className="text-left px-4 py-3 whitespace-nowrap">Business type</th>
                    <th className="text-left px-4 py-3 whitespace-nowrap">Onboarded by</th>
                    <th className="text-left px-4 py-3 whitespace-nowrap">Service start</th>
                    <th className="text-left px-4 py-3 whitespace-nowrap">Last payment</th>
                    <th className="text-left px-4 py-3 whitespace-nowrap">Valid until</th>
                    <th className="text-right px-4 py-3 whitespace-nowrap">Amount</th>
                    <th className="text-left px-4 py-3 whitespace-nowrap">Status</th>
                    <th className="text-right px-4 py-3 whitespace-nowrap">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-stone-100">
                  {filtered.length === 0 ? (
                    <tr>
                      <td colSpan={11} className="px-4 py-8 text-center text-stone-500">
                        No shops found
                      </td>
                    </tr>
                  ) : (
                    filtered.map((t) => (
                      <tr key={t.id} className="hover:bg-stone-50/80">
                        <td className="px-4 py-3 font-medium whitespace-nowrap">{t.shopName}</td>
                        <td className="px-4 py-3 whitespace-nowrap">{t.ownerName}</td>
                        <td className="px-4 py-3 tabular-nums whitespace-nowrap">{t.mobileNumber}</td>
                        <td className="px-4 py-3 text-xs whitespace-nowrap">{businessTypeLabel(t.businessType)}</td>
                        <td className="px-4 py-3 text-xs whitespace-nowrap">
                          {t.onboardedByEmpId ? (
                            <>
                              <span className="font-mono font-medium">{t.onboardedByEmpId}</span>
                              {t.onboardedByName ? (
                                <span className="block text-stone-500">{t.onboardedByName}</span>
                              ) : null}
                            </>
                          ) : (
                            '—'
                          )}
                        </td>
                        <td className="px-4 py-3 tabular-nums whitespace-nowrap">{t.serviceStartDate || '—'}</td>
                        <td className="px-4 py-3 tabular-nums whitespace-nowrap">{t.lastPaymentDate || '—'}</td>
                        <td className="px-4 py-3 tabular-nums whitespace-nowrap">{t.subscriptionEndDate}</td>
                        <td className="px-4 py-3 text-right tabular-nums font-medium whitespace-nowrap">
                          {formatAmount(t.subscriptionPayableAmount ?? t.subscriptionAmount)}
                          {Number(t.subscriptionDiscount) > 0 && (
                            <span className="block text-xs font-normal text-emerald-700">
                              −{formatAmount(t.subscriptionDiscount)}
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">{renderStatusBadge(t)}</td>
                        <td className="px-4 py-3 text-right whitespace-nowrap">
                          {renderTenantActions(t, 'inline')}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {showForm && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
            <form
              onSubmit={saveTenant}
              className="bg-white rounded-xl shadow-xl max-w-lg w-full p-6 space-y-4 max-h-[90vh] overflow-y-auto"
            >
              <h2 className="text-lg font-bold">
                {editingId ? 'Edit shop' : 'Add PocketBillingAI shop'}
              </h2>
              <div className="grid sm:grid-cols-2 gap-3">
                <label className="block text-sm">
                  Owner name
                  <input
                    required
                    className="mt-1 w-full border rounded-lg px-3 py-2"
                    value={form.ownerName}
                    onChange={(e) => setForm({ ...form, ownerName: e.target.value })}
                  />
                </label>
                <label className="block text-sm">
                  Shop name
                  <input
                    required
                    className="mt-1 w-full border rounded-lg px-3 py-2"
                    value={form.shopName}
                    onChange={(e) => setForm({ ...form, shopName: e.target.value })}
                  />
                </label>
              </div>
              <label className="block text-sm">
                Mobile (login)
                <input
                  required
                  type="tel"
                  maxLength={10}
                  className="mt-1 w-full border rounded-lg px-3 py-2"
                  value={form.mobileNumber}
                  onChange={(e) => setForm({ ...form, mobileNumber: e.target.value })}
                  disabled={Boolean(editingId)}
                />
              </label>
              <label className="block text-sm">
                Address
                <input
                  className="mt-1 w-full border rounded-lg px-3 py-2"
                  value={form.address}
                  onChange={(e) => setForm({ ...form, address: e.target.value })}
                />
              </label>
              <label className="block text-sm">
                Business type
                <select
                  required
                  className="mt-1 w-full border rounded-lg px-3 py-2 bg-white"
                  value={form.businessType}
                  onChange={(e) => setForm({ ...form, businessType: e.target.value })}
                >
                  {BUSINESS_TYPE_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
                <span className="text-xs text-stone-500 mt-1 block">
                  Locked for the shop owner in PocketBillingAI. Retail uses product/stock labels;
                  restaurant uses menu/dining labels. Syncs when they sign in.
                </span>
              </label>
              {employeeMode && (
              <div className="rounded-lg border border-indigo-100 bg-indigo-50/50 p-3 space-y-3">
                <p className="text-sm font-semibold text-stone-800">Onboarded by (your account)</p>
                <p className="text-xs text-stone-500">
                  This shop will be linked to your employee profile for tracking and your dashboard
                  stats.
                </p>
                <div className="grid sm:grid-cols-2 gap-3">
                  <label className="block text-sm">
                    Your employee ID *
                    <input
                      required
                      readOnly
                      className="mt-1 w-full border rounded-lg px-3 py-2 font-mono uppercase bg-stone-50 text-stone-800"
                      value={form.onboardedByEmpId}
                    />
                  </label>
                  <label className="block text-sm">
                    Your name *
                    <input
                      required
                      className="mt-1 w-full border rounded-lg px-3 py-2"
                      value={form.onboardedByName}
                      onChange={(e) =>
                        setForm({ ...form, onboardedByName: e.target.value })
                      }
                      placeholder="Full name"
                    />
                  </label>
                </div>
              </div>
              )}
              {!employeeMode && (
              <div className="rounded-lg border border-indigo-100 bg-indigo-50/50 p-3 space-y-3">
                <p className="text-sm font-semibold text-stone-800">Onboarded by (company employee)</p>
                {companyEmployees.length > 0 && (
                  <label className="block text-sm">
                    Pick from employee list
                    <select
                      className="mt-1 w-full border rounded-lg px-3 py-2 bg-white"
                      value={
                        companyEmployees.find((e) => e.empId === form.onboardedByEmpId)?.id || ''
                      }
                      onChange={(e) => pickOnboardingEmployee(e.target.value)}
                    >
                      <option value="">— Select employee —</option>
                      {companyEmployees.map((emp) => (
                        <option key={emp.id} value={emp.id}>
                          {emp.empId} — {emp.name}
                        </option>
                      ))}
                    </select>
                  </label>
                )}
                <div className="grid sm:grid-cols-2 gap-3">
                  <label className="block text-sm">
                    Employee ID *
                    <input
                      required
                      className="mt-1 w-full border rounded-lg px-3 py-2 font-mono uppercase"
                      value={form.onboardedByEmpId}
                      onChange={(e) =>
                        setForm({
                          ...form,
                          onboardedByEmpId: e.target.value.toUpperCase(),
                        })
                      }
                      placeholder="e.g. EMP001"
                    />
                  </label>
                  <label className="block text-sm">
                    Employee name *
                    <input
                      required
                      className="mt-1 w-full border rounded-lg px-3 py-2"
                      value={form.onboardedByName}
                      onChange={(e) =>
                        setForm({ ...form, onboardedByName: e.target.value })
                      }
                      placeholder="Full name"
                    />
                  </label>
                </div>
                <p className="text-xs text-stone-500">
                  Record who onboarded this shop for tracking and reporting. Add employees under
                  Employees in the menu if the list is empty.
                </p>
              </div>
              )}
              <label className="block text-sm">
                Subscription amount (₹)
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  placeholder="e.g. 999"
                  className="mt-1 w-full border rounded-lg px-3 py-2"
                  value={form.subscriptionAmount}
                  onChange={(e) =>
                    setForm({ ...form, subscriptionAmount: e.target.value, subscriptionPlan: '' })
                  }
                />
              </label>
              <label className="block text-sm">
                Subscription duration
                <select
                  className="mt-1 w-full border rounded-lg px-3 py-2 bg-white"
                  value={form.subscriptionPlan}
                  onChange={(e) => setForm((f) => applySubscriptionPlan(f, e.target.value))}
                >
                  <option value="">Custom (set amount & end date manually)</option>
                  {SUBSCRIPTION_PLAN_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label} — ₹{opt.amount.toLocaleString('en-IN')}
                    </option>
                  ))}
                </select>
                <span className="text-xs text-stone-500 mt-1 block">
                  Sets subscription amount and end date. End date uses last payment date if set,
                  otherwise service start date.
                </span>
              </label>
              <label className="block text-sm">
                PocketBillingAI Discount (₹)
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  placeholder="0"
                  className="mt-1 w-full border rounded-lg px-3 py-2"
                  value={form.subscriptionDiscount}
                  onChange={(e) =>
                    setForm({ ...form, subscriptionDiscount: e.target.value.replace(/[^\d.]/g, '') })
                  }
                />
                <span className="text-xs text-stone-500 mt-1 block">
                  Optional PocketBillingAI discount for the shop owner. Subtracted from the
                  subscription amount above.
                </span>
              </label>
              <label className="block text-sm">
                Referral amount credit (₹)
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  placeholder="0"
                  className="mt-1 w-full border rounded-lg px-3 py-2"
                  value={form.referralCredits}
                  onChange={(e) =>
                    setForm({ ...form, referralCredits: e.target.value.replace(/[^\d.]/g, '') })
                  }
                />
                <span className="text-xs text-stone-500 mt-1 block">
                  Credit earned via referrals (or set manually). Auto-increases when referral rewards
                  are granted. Subtracted from subscription after PocketBillingAI discount. Shop
                  owner sees this balance in Settings.
                </span>
              </label>
              {(Number(form.subscriptionDiscount) > 0 || Number(form.referralCredits) > 0) && (
                <div className="rounded-lg border border-indigo-100 bg-indigo-50/60 px-3 py-3 text-sm space-y-1">
                  {Number(form.subscriptionDiscount) > 0 && (
                    <p className="flex justify-between gap-4 text-stone-600">
                      <span>After PocketBillingAI discount</span>
                      <span className="tabular-nums font-medium">
                        {formatAmount(
                          subscriptionPayable(form.subscriptionAmount, form.subscriptionDiscount, 0)
                        )}
                      </span>
                    </p>
                  )}
                  {Number(form.referralCredits) > 0 && (
                    <p className="flex justify-between gap-4 text-emerald-800">
                      <span>Referral credit applied</span>
                      <span className="tabular-nums font-medium">
                        −{formatAmount(form.referralCredits)}
                      </span>
                    </p>
                  )}
                  <p className="flex justify-between gap-4 font-semibold text-indigo-800 pt-1 border-t border-indigo-100">
                    <span>Amount payable by shop owner</span>
                    <span className="tabular-nums">
                      {formatAmount(
                        subscriptionPayable(
                          form.subscriptionAmount,
                          form.subscriptionDiscount,
                          form.referralCredits
                        )
                      )}
                    </span>
                  </p>
                </div>
              )}
              <label className="block text-sm">
                Referral code (referring shop mobile)
                <input
                  type="tel"
                  maxLength={10}
                  placeholder="10-digit mobile of Shop A"
                  className="mt-1 w-full border rounded-lg px-3 py-2 font-mono tabular-nums"
                  value={form.referralCode}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      referralCode: e.target.value.replace(/\D/g, '').slice(0, 10),
                    })
                  }
                  disabled={Boolean(editingId && form.referralLocked)}
                />
                <span className="text-xs text-stone-500 mt-1 block">
                  Optional. When this shop pays subscription, both shops earn ₹100 credit. Locked
                  after first payment. Shop owners can also enter a code in the PocketBillingAI app.
                </span>
              </label>
              <label className="block text-sm">
                Sync email (Google Drive)
                <input
                  type="email"
                  readOnly
                  placeholder="Not linked yet — owner signs in on Sync page"
                  className="mt-1 w-full border rounded-lg px-3 py-2 bg-stone-50 text-stone-800"
                  value={form.syncEmail}
                />
                <span className="text-xs text-stone-500 mt-1 block">
                  Gmail the shop owner uses for backup sync. Updates automatically when they sign in
                  or out of Google on the PocketBillingAI Sync page.
                </span>
              </label>
              {canEditPaymentLink && (
              <label className="block text-sm">
                Payment link
                <input
                  type="url"
                  placeholder="https://razorpay.me/... or UPI link"
                  className="mt-1 w-full border rounded-lg px-3 py-2"
                  value={form.paymentLink}
                  onChange={(e) => setForm({ ...form, paymentLink: e.target.value })}
                />
                <span className="text-xs text-stone-500 mt-1 block">
                  Shown to the shop owner in PocketBillingAI Settings (renewal / subscription payment).
                </span>
              </label>
              )}
              <label className="block text-sm">
                Security PIN (4 digits)
                <div className="mt-1 relative max-w-xs">
                  <input
                    type={showSecurityPin ? 'text' : 'password'}
                    inputMode="numeric"
                    autoComplete="off"
                    maxLength={4}
                    placeholder="e.g. 1234"
                    className="w-full border rounded-lg px-3 py-2 pr-16 tracking-[0.35em] font-mono"
                    value={form.securityPin}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        securityPin: e.target.value.replace(/\D/g, '').slice(0, 4),
                      })
                    }
                  />
                  <button
                    type="button"
                    onClick={() => setShowSecurityPin((v) => !v)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-xs font-medium text-indigo-600 hover:text-indigo-800 px-2 py-1"
                  >
                    {showSecurityPin ? 'Hide' : 'Show'}
                  </button>
                </div>
                <span className="text-xs text-stone-500 mt-1 block">
                  Required in PocketBillingAI when editing or deleting products and bills. Share this PIN
                  with the shop owner. Leave empty to disable PIN protection.
                </span>
              </label>
              <div className="grid sm:grid-cols-3 gap-3">
                <label className="block text-sm">
                  Service start date
                  <input
                    required
                    type="date"
                    className="mt-1 w-full border rounded-lg px-3 py-2"
                    value={form.serviceStartDate}
                    onChange={(e) =>
                      setForm((f) => {
                        const next = { ...f, serviceStartDate: e.target.value };
                        if (f.subscriptionPlan) {
                          next.subscriptionEndDate = endDateForPlan(
                            subscriptionBaseDate(next),
                            f.subscriptionPlan
                          );
                        }
                        return next;
                      })
                    }
                  />
                </label>
                <label className="block text-sm">
                  Last payment date
                  <input
                    type="date"
                    className="mt-1 w-full border rounded-lg px-3 py-2"
                    value={form.lastPaymentDate}
                    onChange={(e) =>
                      setForm((f) => {
                        const next = { ...f, lastPaymentDate: e.target.value };
                        if (f.subscriptionPlan) {
                          next.subscriptionEndDate = endDateForPlan(
                            subscriptionBaseDate(next),
                            f.subscriptionPlan
                          );
                        }
                        return next;
                      })
                    }
                  />
                  <span className="text-xs text-stone-500 mt-1 block">
                    When subscription payment was received. Leave empty if not paid yet.
                  </span>
                </label>
                <label className="block text-sm">
                  Subscription end date
                  <input
                    required
                    type="date"
                    className="mt-1 w-full border rounded-lg px-3 py-2"
                    value={form.subscriptionEndDate}
                    onChange={(e) =>
                      setForm({ ...form, subscriptionEndDate: e.target.value, subscriptionPlan: '' })
                    }
                  />
                </label>
              </div>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={form.isActive}
                  onChange={(e) => setForm({ ...form, isActive: e.target.checked })}
                />
                Account active (uncheck to block login)
              </label>
              <label className="block text-sm">
                Notes
                <textarea
                  rows={2}
                  className="mt-1 w-full border rounded-lg px-3 py-2"
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                />
              </label>
              <div className="flex gap-2 justify-end">
                <button
                  type="button"
                  className="px-4 py-2 rounded-lg border text-sm"
                  onClick={() => setShowForm(false)}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={busy}
                  className="px-4 py-2 rounded-lg bg-indigo-600 text-white text-sm font-medium disabled:opacity-50"
                >
                  {busy ? 'Saving…' : 'Save'}
                </button>
              </div>
            </form>
          </div>
        )}

        {extendId && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
            <form
              onSubmit={saveExtend}
              className="bg-white rounded-xl shadow-xl max-w-sm w-full p-6 space-y-4"
            >
              <h2 className="text-lg font-bold">Extend subscription</h2>
              <label className="block text-sm">
                New end date
                <input
                  required
                  type="date"
                  className="mt-1 w-full border rounded-lg px-3 py-2"
                  value={extendDate}
                  onChange={(e) => setExtendDate(e.target.value)}
                />
              </label>
              <div className="flex gap-2 justify-end">
                <button
                  type="button"
                  className="px-4 py-2 rounded-lg border text-sm"
                  onClick={() => setExtendId(null)}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={busy}
                  className="px-4 py-2 rounded-lg bg-indigo-600 text-white text-sm font-medium disabled:opacity-50"
                >
                  Update validity
                </button>
              </div>
            </form>
          </div>
        )}
      </div>
    </Layout>
  );
}
